import { FC, memo, useState } from 'react'
import styled, { css } from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import { Product, ProductSize, PromoCode } from '@/network/graphql.g'
import { Flex, Grid } from '@/components/atoms/Grid'
import { Tiny2 } from '@/components/atoms/typography'
import { ArrowDownIconDefault } from '@/components/atoms/icons/ArrowDownIconDefault'
import ProductDetailSizeTable from '@/components/organisms/productDetailSideMenu/productDetailSizeTable'
import { useSizeUrlSync } from '@/components/organisms/productDetailSideMenu/useSizeUrlSync'
import ProductDetailSizeBoxNew from './productDetailSizeBoxNew'

const MAX_VISIBLE_SIZES = 12

type ProductDetailSizesNewProps = {
  product: Product
  sizes: ProductSize[]
  initialSelectedSizeId?: string
  onDidSelectSize: (sizeId: string) => void
  promoCodes: PromoCode[]
  // The customer tried to add to cart without a size.
  hasError?: boolean
}

const ProductDetailSizesNew: FC<ProductDetailSizesNewProps> = ({
  product,
  sizes,
  initialSelectedSizeId,
  onDidSelectSize,
  promoCodes,
  hasError = false
}) => {
  const { t } = useTranslation('common')
  const [showAllSizes, setShowAllSizes] = useState<boolean>(false)

  const { selectedSizeId, selectSize } = useSizeUrlSync({
    sizes,
    initialSelectedSizeId,
    onDidSelectSize
  })

  const isMaxSizesDisplayReached = sizes.length > MAX_VISIBLE_SIZES
  const displaySizes =
    isMaxSizesDisplayReached && !showAllSizes
      ? sizes.slice(0, MAX_VISIBLE_SIZES)
      : sizes

  const selectedSizeValue = sizes.find(
    (size) => size.id === selectedSizeId
  )?.value
  const showError = hasError && !selectedSizeValue

  return (
    <Flex flexDirection="column">
      <Flex alignItems="center" justifyContent="space-between" mb="8px">
        <Label m={0} hasError={showError}>
          {showError ? (
            t('ProductDetail.Sizes.chooseSizeFirst')
          ) : (
            <>
              {`${t('ProductDetail.Sizes.chooseSize')}: `}
              <SelectedValue>{selectedSizeValue}</SelectedValue>
            </>
          )}
        </Label>
        {!product?.disableSizeChart && (
          <ProductDetailSizeTable
            productId={product?.id}
            productBrand={product?.productBrand}
            name={product?.name}
            supplierId={product?.MHubProperties?.supplierId}
            category={product?.MHubProperties?.category}
            iconPosition="right"
          />
        )}
      </Flex>
      <SizesGrid data-testid="productDetailSizesDelivery">
        {displaySizes.map(
          ({ id, value, delivery, available, isStock }, offset) => (
            <ProductDetailSizeBoxNew
              key={id}
              id={id}
              value={value}
              delivery={delivery}
              available={available}
              isStock={isStock}
              isSelected={selectedSizeId === id}
              hasError={showError}
              product={product}
              position={offset + 1}
              promoCodes={promoCodes}
              onClick={() => selectSize(id)}
            />
          )
        )}
      </SizesGrid>
      {isMaxSizesDisplayReached && (
        <ShowMoreSizes
          onClick={() => setShowAllSizes((isShown) => !isShown)}
          showAllSizes={showAllSizes}
        >
          <Tiny2 mr={1}>{t('ProductDetail.Sizes.showAllSizes')}</Tiny2>
          <StyledDownIcon showAllSizes={showAllSizes} />
        </ShowMoreSizes>
      )}
    </Flex>
  )
}

export default memo(ProductDetailSizesNew)

const Label = styled(Tiny2)`
  font-weight: normal;
  letter-spacing: -0.12px;
  ${(props) =>
    props.hasError &&
    `
    color: ${props.theme.colors.error};
    font-weight: bold;
  `}
`

const SelectedValue = styled.span`
  font-weight: bold;
`

const SizesGrid = styled(Grid)`
  grid-template-columns: repeat(6, 1fr);
  grid-gap: 12px;
`

const ShowMoreSizes = styled(Flex)`
  margin-top: 12px;
  align-items: center;
  &:hover {
    cursor: pointer;
  }
`

const StyledDownIcon = styled(ArrowDownIconDefault)`
  transition: all 0.3s ease-out;
  ${({ showAllSizes }) =>
    showAllSizes &&
    css`
      transform: rotateZ(180deg);
      transform-origin: center;
    `};
`
